import { useEffect, useMemo, useRef, useState } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/Button";
import { SharedChannelApiError, SharedChannelsApi, type SharedChannel } from "./api";
import { getSession, sessionScope, useSession } from "@/lib/auth";
import { useMembers, useWorkspaceMemberships } from "@/lib/workspace";
import { authenticatedFetch, resolveApiUrl } from "@/lib/api";
import { useWebSocket } from "@/lib/ws";
import { useSearchParams } from "react-router-dom";

const MAX_FILE = 10 * 1024 * 1024;
const STATUS_LABEL: Record<SharedChannel["status"], string> = {
  pending: "承諾待ち",
  active: "利用中",
  disconnected: "切断済み",
  expired: "期限切れ",
};

function newClientId() {
  return `web-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

async function fileToPayload(file: File) {
  if (file.size > MAX_FILE) throw new Error("ファイルは 10MB までです。");
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return { filename: file.name, mime_type: file.type || "application/octet-stream", bytes_base64: btoa(binary) };
}

export function SharedChannelsPanel() {
  const session = useSession();
  return <SharedChannelsContent key={sessionScope(session)} />;
}

function SharedChannelsContent() {
  const api = useMemo(() => new SharedChannelsApi(), []);
  const session = useSession();
  const scope = sessionScope(session);
  const [params] = useSearchParams();
  const appliedLink = useRef<string | null>(null);
  const memberships = useWorkspaceMemberships();
  const membersQuery = useMembers();
  const role = memberships.data?.workspaces.find((w) => w.active)?.role ?? membersQuery.workspace?.role;
  const isAdmin = role === "owner" || role === "admin";
  const orgMembers = membersQuery.members ?? [];

  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [partner, setPartner] = useState("");
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [expiresDays, setExpiresDays] = useState("30");
  const [selected, setSelected] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [searchQ, setSearchQ] = useState("");
  const [searchAuthor, setSearchAuthor] = useState("");
  const [searchHasFile, setSearchHasFile] = useState(false);
  const [acceptMembers, setAcceptMembers] = useState<string[]>([]);
  const clientId = useRef<string | null>(null);
  const [busy, setBusy] = useState(false);
  const alive = useRef(true);
  const selectedRef = useRef(selected); selectedRef.current = selected;
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const current = () => alive.current && sessionScope(getSession()) === scope;

  useEffect(() => {
    setSelected(null);
    setDraft("");
    setFile(null);
    setName("");
    setPartner("");
    setSelectedMembers([]);
    setSearchQ("");
    setSearchAuthor("");
    setError(null);
    clientId.current = null;
  }, [scope]);
  useEffect(() => { setDraft(""); setFile(null); setSearchQ(""); setSearchAuthor(""); setSearchHasFile(false); setAcceptMembers([]); clientId.current = null; }, [selected]);

  const list = useQuery({
    queryKey: ["shared-channels", scope],
    enabled: !!session,
    queryFn: () => api.list(),
  });
  const messages = useInfiniteQuery({
    queryKey: ["shared-channel-messages", scope, selected],
    enabled: !!selected && !!session,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => api.messages(selected!, pageParam),
    getNextPageParam: page => page.next_cursor ?? undefined,
  });
  const channelMembers = useQuery({
    queryKey: ["shared-channel-members", scope, selected],
    enabled: !!selected && !!session,
    queryFn: () => api.members(selected!),
  });
  const notices = useQuery({
    queryKey: ["shared-channel-notifications", scope],
    enabled: !!session,
    queryFn: () => api.notifications(),
  });
  const search = useQuery({
    queryKey: ["shared-channel-search", scope, selected, searchQ, searchAuthor, searchHasFile],
    enabled: !!selected && !!session && (searchQ.trim().length > 0 || searchHasFile || !!searchAuthor),
    queryFn: () => api.search({ q: searchQ, channel_id: selected ?? undefined, author_user_id: searchAuthor || undefined, has_attachment: searchHasFile }),
  });

  const selectedChannel = (list.data?.channels ?? []).find((ch) => ch.id === selected) ?? null;
  const canManageSelected = !!selectedChannel?.can_manage;
  const canPost = !!role && !["viewer", "guest"].includes(role);
  const messageList = useMemo(() => {
    const unique = new Map((messages.data?.pages ?? []).flatMap(page => page.messages).map(message => [message.id, message]));
    return [...unique.values()];
  }, [messages.data]);
  const tenantId = session?.tenantId ?? memberships.data?.workspaces.find((w) => w.active)?.tenant_id;
  const canAccept = !!isAdmin && selectedChannel?.status === "pending" && selectedChannel.partner_tenant_id === tenantId;
  useEffect(() => {
    const requested = params.get("channel");
    const requestedTenant = params.get("tenant");
    if (!requested || !list.data || (requestedTenant && requestedTenant !== tenantId) || appliedLink.current === requested) return;
    appliedLink.current = requested;
    if (list.data.channels.some(channel => channel.id === requested)) setSelected(requested);
    else setError("この共有チャンネルは利用できません。参加権限をご確認ください。");
  }, [params, list.data, tenantId]);

  const wsUrl = selected
    ? (() => {
        const address = new URL(resolveApiUrl(`/v1/shared-channels/${encodeURIComponent(selected)}/events`), window.location.origin);
        address.protocol = address.protocol === "https:" ? "wss:" : "ws:";
        return address.toString();
      })()
    : "";
  const connection = useWebSocket(wsUrl, {
    token: session?.token,
    authProtocol: "aile-shared-v1",
    enabled: !!selected && !!session && selectedChannel?.status === "active",
    onMessage: () => {
      void messages.refetch();
      void list.refetch();
      void notices.refetch();
    },
    onOpen: () => { void messages.refetch(); },
    onDenied: () => { setSelected(null); setError("共有チャンネルへのアクセスが失効しました。"); void list.refetch(); },
  });

  useEffect(() => {
    const latest = messages.data?.pages[0]?.messages[0];
    if (!selected || !latest || !current()) return;
    void api.markRead(selected, latest.id).then(() => { if (current()) void notices.refetch(); }).catch(e => { if (current()) setError(e instanceof Error ? e.message : String(e)); });
  }, [api, messages.data?.pages[0]?.messages[0]?.id, selected]);

  const run = async (op: () => Promise<void>) => {
    if (!current() || busy) return;
    setError(null);
    setBusy(true);
    const target = selected;
    try {
      await op();
      if (!current()) return;
      await list.refetch();
      if (selected && selectedRef.current === target) {
        await messages.refetch();
        await channelMembers.refetch();
      }
      await notices.refetch();
    } catch (e) {
      if (current()) setError(e instanceof SharedChannelApiError || e instanceof Error ? e.message : String(e));
    } finally { if (current()) setBusy(false); }
  };

  const partnerLabel = (ch: SharedChannel) => {
    const selfId = memberships.data?.workspaces.find((w) => w.active)?.tenant_id;
    if (selfId && ch.owner_tenant_id === selfId) return ch.partner_name || "相手組織";
    return ch.owner_name || "相手組織";
  };

  return (
    <section className="space-y-4" data-testid="shared-channels-panel">
      <header>
        <h2 className="text-lg font-bold">組織間の共有チャンネル</h2>
        <p className="text-sm text-fg-secondary">
          相手組織の管理者の承諾が必要です。各社のメンバーは自組織のまま参加します。選択したメンバーだけが会話できます。
        </p>
      </header>
      {error && <p role="alert" className="text-danger">{error}</p>}
      {[messages.error, channelMembers.error, notices.error, search.error].filter(Boolean).map((failure, index) => <p key={index} role="alert" className="text-danger">{failure instanceof Error ? failure.message : "読み込めませんでした。"}</p>)}
      {(notices.data?.unread_count ?? 0) > 0 && (
        <p className="text-sm">未読 {notices.data?.unread_count} 件</p>
      )}
      {isAdmin && (
        <form
          className="space-y-2"
          onSubmit={(e) => {
            e.preventDefault();
            const days = Number(expiresDays);
            void run(async () => {
              await api.create({
                name,
                partner_tenant_id: partner.trim(),
                member_user_ids: selectedMembers,
                expires_at: Date.now() + Math.max(1, Number.isFinite(days) ? days : 30) * 24 * 60 * 60 * 1000,
              });
              setName("");
              setPartner("");
              setSelectedMembers([]);
            });
          }}
        >
          <label className="block text-sm">名前<input className="ml-2 border rounded px-2 py-1" value={name} onChange={(e) => setName(e.target.value)} /></label>
          <label className="block text-sm">
            相手のワークスペース
            <input className="ml-2 border rounded px-2 py-1" value={partner} onChange={(e) => setPartner(e.target.value)} aria-describedby="partner-help" />
          </label>
          <p id="partner-help" className="text-xs text-fg-secondary">相手組織から共有されたワークスペースの識別子を入力します。</p>
          <fieldset className="text-sm">
            <legend>自組織の参加メンバー</legend>
            {orgMembers.map((member) => (
              <label key={member.id} className="mr-3 inline-flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={selectedMembers.includes(member.id)}
                  onChange={(e) => setSelectedMembers((cur) => e.target.checked ? [...cur, member.id] : cur.filter((id) => id !== member.id))}
                />
                {member.displayName}
              </label>
            ))}
          </fieldset>
          <label className="block text-sm">有効日数<input className="ml-2 border rounded px-2 py-1 w-20" value={expiresDays} onChange={(e) => setExpiresDays(e.target.value)} /></label>
          <Button type="submit">招待を送る</Button>
        </form>
      )}
      {list.isError && <p role="alert">一覧を読み込めませんでした。</p>}
      <ul>
        {(list.data?.channels ?? []).map((ch) => (
          <li key={ch.id} className="flex flex-wrap gap-2 items-center py-1">
            <button type="button" className="underline" onClick={() => setSelected(ch.id)}>{ch.name}</button>
            <span>{STATUS_LABEL[ch.status]}</span>
            <span className="text-sm text-fg-secondary">{partnerLabel(ch)}</span>
            {(ch.unread_count ?? 0) > 0 && <span>未読 {ch.unread_count}</span>}
            {isAdmin && ch.status === "pending" && ch.can_manage && (
              <Button size="sm" onClick={() => setSelected(ch.id)}>詳細</Button>
            )}
            {isAdmin && ch.status !== "disconnected" && ch.can_manage && (
              <Button size="sm" variant="secondary" onClick={() => void run(async () => { await api.disconnect(ch.id); })}>切断</Button>
            )}
          </li>
        ))}
      </ul>
      {selectedChannel && (
        <div className="space-y-2">
          <h3 className="font-medium">{selectedChannel.name}</h3>
          <p className="text-sm text-fg-secondary">
            {selectedChannel.owner_name} と {selectedChannel.partner_name} の間で、選んだメンバーだけが参加します。
            {selectedChannel.expires_at ? ` 期限: ${new Date(selectedChannel.expires_at).toLocaleDateString()}` : ""}
          </p>
          {canAccept && (
            <form
              className="space-y-2"
              onSubmit={(e) => {
                e.preventDefault();
                void run(async () => { await api.accept(selectedChannel.id, acceptMembers); });
              }}
            >
              <p>相手組織: {selectedChannel.owner_name}</p>
              <p>共有範囲: 両組織で選んだメンバーのみ。通常の部屋には混ざりません。</p>
              <fieldset className="text-sm">
                <legend>自組織から参加させるメンバー</legend>
                {orgMembers.map((member) => (
                  <label key={member.id} className="mr-3 inline-flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={acceptMembers.includes(member.id)}
                      onChange={(e) => setAcceptMembers((cur) => e.target.checked ? [...cur, member.id] : cur.filter((id) => id !== member.id))}
                    />
                    {member.displayName}
                  </label>
                ))}
              </fieldset>
              <Button type="submit">承諾</Button>
            </form>
          )}
          {canManageSelected && selectedChannel.status === "active" && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const pick = new FormData(e.currentTarget).get("add-member");
                if (typeof pick === "string" && pick) void run(async () => { await api.addMembers(selectedChannel.id, [pick]); });
              }}
            >
              <label className="text-sm">メンバーを追加
                <select name="add-member" className="ml-2 border rounded px-2 py-1">
                  {orgMembers.map((member) => <option key={member.id} value={member.id}>{member.displayName}</option>)}
                </select>
              </label>
              <Button type="submit" size="sm" className="ml-2">追加</Button>
            </form>
          )}
          <ul className="text-sm">
            {(channelMembers.data?.members ?? []).map((member) => (
              <li key={`${member.tenant_id}:${member.user_id}`}>
                {member.display_name || "相手組織のメンバー"}
                {canManageSelected && member.tenant_id === membersQuery.workspace?.tenant_id && (
                  <Button size="sm" variant="ghost" onClick={() => void run(async () => { await api.removeMember(selectedChannel.id, member.user_id); })}>外す</Button>
                )}
              </li>
            ))}
          </ul>
          {selectedChannel.status === "active" && (
            <>
              <form
                className="flex flex-wrap gap-2 items-end"
                onSubmit={(e) => {
                  e.preventDefault();
                  void search.refetch();
                }}
              >
                <label className="text-sm">検索<input className="ml-2 border rounded px-2 py-1" value={searchQ} onChange={(e) => setSearchQ(e.target.value)} /></label>
                <label className="text-sm">投稿者
                  <input className="ml-2 border rounded px-2 py-1" value={searchAuthor} onChange={(e) => setSearchAuthor(e.target.value)} />
                </label>
                <label className="text-sm"><input type="checkbox" checked={searchHasFile} onChange={(e) => setSearchHasFile(e.target.checked)} /> 添付あり</label>
                <Button type="submit" size="sm">検索</Button>
              </form>
              {search.data?.hits?.length ? (
                <ul>
                  {search.data.hits.map((hit) => (
                    <li key={hit.id}>{hit.body_md}</li>
                  ))}
                </ul>
              ) : null}
              <h3 className="font-medium">投稿</h3>
              <p className="text-xs text-fg-secondary" role="status">{connection.state === "open" ? "接続中" : "再接続しています…"}</p>
              {messages.hasNextPage && (
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={messages.isFetchingNextPage}
                  onClick={() => void messages.fetchNextPage()}
                >
                  以前の投稿を表示
                </Button>
              )}
              <ul>
                {messageList.map((m) => (
                  <li key={m.id}>
                    {m.body_md}
                    {m.attachment && (
                      <button
                        type="button"
                        className="ml-2 underline"
                        onClick={() => {
                          void authenticatedFetch(api.downloadUrl(selectedChannel.id, m.attachment!.id)).then(async (res) => {
                            if (!res.ok) throw new Error(`添付を取得できませんでした (${res.status})。`);
                            const blob = await res.blob();
                            if (!current()) return;
                            const url = URL.createObjectURL(blob);
                            const link = document.createElement("a");
                            link.href = url;
                            link.download = m.attachment!.filename;
                            link.click();
                            URL.revokeObjectURL(url);
                          }).catch(e => { if (current()) setError(e instanceof Error ? e.message : String(e)); });
                        }}
                      >
                        {m.attachment.filename}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
              {canPost ? <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void run(async () => {
                    if (!clientId.current) clientId.current = newClientId();
                    const sentClientId = clientId.current;
                    const attachment = file ? await fileToPayload(file) : null;
                    if (!current() || selectedRef.current !== selectedChannel.id) return;
                    await api.post(selectedChannel.id, draft, sentClientId, attachment);
                    if (!current() || selectedRef.current !== selectedChannel.id) return;
                    clientId.current = null;
                    setDraft("");
                    setFile(null);
                  });
                }}
              >
                <input aria-label="共有チャンネルの本文" disabled={busy} className="border rounded px-2 py-1 mr-2" value={draft} onChange={(e) => { setDraft(e.target.value); clientId.current = null; }} />
                <input aria-label="添付ファイル" disabled={busy} type="file" onChange={(e) => { setFile(e.target.files?.[0] ?? null); clientId.current = null; }} />
                <Button type="submit" disabled={busy || (!draft.trim() && !file)}>送信</Button>
              </form> : <p className="text-sm text-fg-secondary">このワークスペースの権限では閲覧のみ可能です。</p>}
            </>
          )}
        </div>
      )}
    </section>
  );
}
